import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Filter,
  RefreshCw,
  Calculator,
  Sparkles,
  TrendingUp,
  Package,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import {
  getPricingRulesThunk,
  deletePricingRuleThunk,
  togglePricingRuleStatusThunk,
} from "../../store/thunks/pricingThunks";
import {
  selectPricingRules,
  selectPricingRulesLoading,
  selectPricingRulesError,
  selectSelectedRuleIds,
  selectPricingFilters,
  selectPricingPagination,
  setSelectedRuleIds,
  deselectAllRules,
  setFilters,
  clearFilters,
  setPage,
} from "../../store/slices/pricingSlice";
import PricingRuleCard from "./components/PricingRuleCard";
import PricingRuleFilters from "./components/PricingRuleFilters";
import PricingRulesDashboard from "./components/PricingRulesDashboard";
import BulkActionsToolbar from "./components/BulkActionsToolbar";
import PricingCalculator from "./components/PricingCalculator";
import Pagination from "../../components/common/Pagination";

// Skeleton loader component for better loading UX
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-neutral-light/50 rounded-xl" />
        <div>
          <div className="h-5 w-32 bg-neutral-light/50 rounded-lg mb-2" />
          <div className="h-3 w-20 bg-neutral-light/30 rounded" />
        </div>
      </div>
      <div className="w-16 h-6 bg-neutral-light/50 rounded-full" />
    </div>
    <div className="space-y-2 mb-4">
      <div className="h-3 w-full bg-neutral-light/30 rounded" />
      <div className="h-3 w-3/4 bg-neutral-light/30 rounded" />
    </div>
    <div className="flex items-center justify-between pt-4 border-t border-neutral-light/50">
      <div className="h-4 w-24 bg-neutral-light/30 rounded" />
      <div className="flex space-x-2">
        <div className="w-8 h-8 bg-neutral-light/30 rounded-lg" />
        <div className="w-8 h-8 bg-neutral-light/30 rounded-lg" />
      </div>
    </div>
  </div>
);

// Empty state component
const EmptyState = ({ hasFilters, onClear, onCreate }) => (
  <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center">
      <Package className="w-10 h-10 text-primary" />
    </div>
    <h3 className="text-xl font-semibold text-neutral-dark mb-2">
      {hasFilters ? "No matching rules found" : "No pricing rules yet"}
    </h3>
    <p className="text-neutral mb-6 max-w-md mx-auto">
      {hasFilters
        ? "Try adjusting your search or filter criteria to find what you're looking for."
        : "Create your first pricing rule to start managing product prices automatically."}
    </p>
    <button
      onClick={hasFilters ? onClear : onCreate}
      className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-300"
    >
      {hasFilters ? (
        <>
          <X className="w-5 h-5" />
          <span>Clear Filters</span>
        </>
      ) : (
        <>
          <Plus className="w-5 h-5" />
          <span>Create First Rule</span>
        </>
      )}
    </button>
  </div>
);

// Stats card for the header area
const QuickStat = ({ icon: Icon, label, value, trend, color = "primary" }) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-neutral-light/50 hover:shadow-md transition-all duration-300">
    <div className="flex items-center justify-between">
      <div
        className={`w-10 h-10 rounded-xl bg-${color}/10 flex items-center justify-center`}
      >
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      {trend && (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            trend > 0
              ? "bg-success-light text-success-dark"
              : "bg-danger-light text-danger-dark"
          }`}
        >
          {trend > 0 ? "+" : ""}
          {trend}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold text-neutral-dark mt-3">{value}</p>
    <p className="text-sm text-neutral">{label}</p>
  </div>
);

const PricingRuleList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pricingRules = useSelector(selectPricingRules);
  const loading = useSelector(selectPricingRulesLoading);
  const error = useSelector(selectPricingRulesError);
  const selectedRuleIds = useSelector(selectSelectedRuleIds);
  const filters = useSelector(selectPricingFilters);
  const pagination = useSelector(selectPricingPagination);

  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCalculator, setShowCalculator] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(
      (v) => v !== null && v !== "" && v !== undefined
    ).length;
  }, [filters]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      dispatch(setFilters({ ...filters, search: query }));
      dispatch(setPage(1));
    }, 500),
    [dispatch, filters]
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Initial load
  useEffect(() => {
    dispatch(
      getPricingRulesThunk({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      })
    ).finally(() => {
      setIsInitialLoad(false);
    });
  }, [dispatch, filters, pagination.page, pagination.limit]);

  // Client-side filtering
  const filteredRules = useMemo(() => {
    let filtered = [...pricingRules];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (rule) =>
          rule.name.toLowerCase().includes(query) ||
          rule.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [pricingRules, searchQuery]);

  // Quick stats
  const stats = useMemo(() => {
    const active = pricingRules.filter((r) => r.isActive).length;
    const inactive = pricingRules.length - active;
    return { total: pricingRules.length, active, inactive };
  }, [pricingRules]);

  // Bulk action handlers
  const handleBulkActivate = async () => {
    const rulesToUpdate = selectedRuleIds;
    try {
      toast.loading("Activating rules...", { id: "bulk-activate" });
      await Promise.all(
        rulesToUpdate.map((id) =>
          dispatch(togglePricingRuleStatusThunk(id)).unwrap()
        )
      );
      toast.success(`${rulesToUpdate.length} rules activated`, {
        id: "bulk-activate",
      });
      dispatch(deselectAllRules());
    } catch (error) {
      toast.error("Failed to activate some rules", { id: "bulk-activate" });
    }
  };

  const handleBulkDeactivate = async () => {
    const rulesToUpdate = selectedRuleIds;
    try {
      toast.loading("Deactivating rules...", { id: "bulk-deactivate" });
      await Promise.all(
        rulesToUpdate.map((id) =>
          dispatch(togglePricingRuleStatusThunk(id)).unwrap()
        )
      );
      toast.success(`${rulesToUpdate.length} rules deactivated`, {
        id: "bulk-deactivate",
      });
      dispatch(deselectAllRules());
    } catch (error) {
      toast.error("Failed to deactivate some rules", { id: "bulk-deactivate" });
    }
  };

  const handleBulkDelete = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedRuleIds.length} rules?`
      )
    ) {
      return;
    }

    const rulesToDelete = selectedRuleIds;
    try {
      toast.loading("Deleting rules...", { id: "bulk-delete" });
      await Promise.all(
        rulesToDelete.map((id) => dispatch(deletePricingRuleThunk(id)).unwrap())
      );
      toast.success(`${rulesToDelete.length} rules deleted`, {
        id: "bulk-delete",
      });
      dispatch(deselectAllRules());
    } catch (error) {
      toast.error("Failed to delete some rules", { id: "bulk-delete" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this pricing rule?")) {
      try {
        toast.loading("Deleting rule...", { id: "delete-rule" });
        await dispatch(deletePricingRuleThunk(id)).unwrap();
        toast.success("Pricing rule deleted successfully", {
          id: "delete-rule",
        });
      } catch (error) {
        toast.error(error || "Failed to delete pricing rule", {
          id: "delete-rule",
        });
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await dispatch(togglePricingRuleStatusThunk(id)).unwrap();
      toast.success("Rule status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleSelectAll = () => {
    if (selectedRuleIds.length === filteredRules.length) {
      dispatch(deselectAllRules());
    } else {
      dispatch(setSelectedRuleIds(filteredRules.map((r) => r._id)));
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await dispatch(
        getPricingRulesThunk({
          ...filters,
          page: pagination.page,
          limit: pagination.limit,
        })
      ).unwrap();
      toast.success("Rules refreshed");
    } catch (error) {
      toast.error("Failed to refresh");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    dispatch(clearFilters());
  };

  // Error state
  if (error && isInitialLoad) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-lightest via-white to-primary/5 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white border border-danger/20 rounded-2xl p-8 text-center shadow-lg">
            <div className="w-16 h-16 mx-auto mb-4 bg-danger-light rounded-full flex items-center justify-center">
              <X className="w-8 h-8 text-danger" />
            </div>
            <h3 className="text-xl font-semibold text-neutral-dark mb-2">
              Error Loading Pricing Rules
            </h3>
            <p className="text-neutral mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-lightest via-white to-primary/5">
      {/* Header Section with Gradient Background */}
      <div className="bg-gradient-to-r from-primary/5 via-secondary/5 to-primary/5 border-b border-neutral-light/50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Top Row: Title & Actions */}
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center shadow-lg shadow-primary/25">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-dark to-secondary bg-clip-text text-transparent">
                  Pricing Rules
                </h1>
              </div>
              <p className="text-neutral ml-13">
                Manage and automate your product pricing strategies
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowCalculator(true)}
                className="group flex items-center space-x-2 px-4 py-2.5 bg-white border border-neutral-light text-neutral-dark rounded-xl hover:border-primary/30 hover:shadow-md transition-all duration-300"
              >
                <Calculator className="w-5 h-5 text-neutral group-hover:text-primary transition-colors" />
                <span className="font-medium">Calculator</span>
              </button>
              <button
                onClick={() => navigate("/pricing-rules/new")}
                className="flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-300"
              >
                <Plus className="w-5 h-5" />
                <span>Create Rule</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Dashboard Stats */}
        {!isInitialLoad && <PricingRulesDashboard rules={pricingRules} />}

        {/* Bulk Actions Toolbar */}
        <BulkActionsToolbar
          selectedCount={selectedRuleIds.length}
          onActivate={handleBulkActivate}
          onDeactivate={handleBulkDeactivate}
          onDelete={handleBulkDelete}
          onClearSelection={() => dispatch(deselectAllRules())}
        />

        {/* Search and Filters Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-light/50 p-5 mb-6 transition-all duration-300">
          <div className="flex items-center gap-4">
            {/* Search Input */}
            <div className="flex-1 relative group">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral w-5 h-5 group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search by name or description..."
                className="w-full pl-12 pr-10 py-3 bg-neutral-lightest/50 border border-transparent rounded-xl focus:bg-white focus:border-primary/30 focus:ring-4 focus:ring-primary/10 transition-all duration-300 placeholder:text-neutral"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    debouncedSearch("");
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center text-neutral hover:text-neutral-dark hover:bg-neutral-light rounded-full transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filter Toggle Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`relative flex items-center space-x-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${
                showFilters
                  ? "bg-primary text-white shadow-lg shadow-primary/25"
                  : "bg-neutral-lightest/50 text-neutral-dark hover:bg-neutral-light/50"
              }`}
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span
                  className={`absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full ${
                    showFilters
                      ? "bg-white text-primary"
                      : "bg-primary text-white"
                  }`}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Select All Button */}
            <button
              onClick={handleSelectAll}
              disabled={filteredRules.length === 0}
              className="px-5 py-3 bg-neutral-lightest/50 text-neutral-dark rounded-xl font-medium hover:bg-neutral-light/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              {selectedRuleIds.length === filteredRules.length &&
              filteredRules.length > 0
                ? "Deselect All"
                : "Select All"}
            </button>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-3 bg-neutral-lightest/50 text-neutral-dark rounded-xl hover:bg-neutral-light/50 disabled:opacity-50 transition-all duration-300"
              title="Refresh rules"
            >
              <RefreshCw
                className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </button>
          </div>

          {/* Expandable Filters Panel */}
          <div
            className={`grid transition-all duration-300 ease-in-out ${
              showFilters
                ? "grid-rows-[1fr] opacity-100 mt-5 pt-5 border-t border-neutral-light/50"
                : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="overflow-hidden">
              <PricingRuleFilters
                filters={filters}
                onFilterChange={(newFilters) =>
                  dispatch(setFilters(newFilters))
                }
                onClearFilters={handleClearFilters}
              />
            </div>
          </div>
        </div>

        {/* Rules List Section */}
        {isInitialLoad ? (
          // Skeleton Loading State
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : filteredRules.length === 0 ? (
          // Empty State
          <EmptyState
            hasFilters={searchQuery || activeFilterCount > 0}
            onClear={handleClearFilters}
            onCreate={() => navigate("/pricing-rules/new")}
          />
        ) : (
          <>
            {/* Loading Overlay for Refresh */}
            <div className="relative">
              {loading && !isInitialLoad && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-2xl">
                  <div className="flex items-center space-x-3 px-6 py-3 bg-white rounded-full shadow-lg">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="font-medium text-neutral-dark">
                      Updating...
                    </span>
                  </div>
                </div>
              )}

              {/* Rules Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {filteredRules.map((rule, index) => (
                  <div
                    key={rule._id}
                    className="opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards]"
                    style={{
                      animationDelay: `${index * 60}ms`,
                    }}
                  >
                    <PricingRuleCard
                      rule={rule}
                      isSelected={selectedRuleIds.includes(rule._id)}
                      onSelect={() => {
                        if (selectedRuleIds.includes(rule._id)) {
                          dispatch(
                            setSelectedRuleIds(
                              selectedRuleIds.filter((id) => id !== rule._id)
                            )
                          );
                        } else {
                          dispatch(
                            setSelectedRuleIds([...selectedRuleIds, rule._id])
                          );
                        }
                      }}
                      onEdit={() => navigate(`/pricing-rules/${rule._id}/edit`)}
                      onClone={() =>
                        navigate(`/pricing-rules/${rule._id}/clone`)
                      }
                      onDelete={() => handleDelete(rule._id)}
                      onToggleStatus={() => handleToggleStatus(rule._id)}
                      onViewDetails={() =>
                        navigate(`/pricing-rules/${rule._id}`)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center pt-4">
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={(page) => dispatch(setPage(page))}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Pricing Calculator Modal */}
      {showCalculator && (
        <PricingCalculator onClose={() => setShowCalculator(false)} />
      )}

      {/* Custom Animation Styles */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default PricingRuleList;
